import { PageFrame } from "../../../../../../shared/frontend/layout/PageFrame";
import { MessageOutlined } from "@ant-design/icons";
import type { ListColumn } from "../../../../../../shared/frontend/list/listColumn";
import { Button, Tabs, Typography } from "../../../../../../shared/frontend/ui";
import React from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../app/auth/useAuth";
import { useI18n } from "../../app/i18n/useI18n";
import { useChannelAvailability, type ChannelFlags } from "../../app/integrations/useChannelAvailability";
import { useRealtimeListRefresh } from "../../app/realtime/RealtimeContext";
import { RealtimeEventTypes } from "../../app/realtime/realtimeEventTypes";
import { useGlobalStudioContext } from "../../app/studios/useGlobalStudioContext";
import { formatDateTimeNoSeconds } from "../../app/units/format";
import { getActiveRequestSort } from "../../app/utils/proTableSortPersistence";
import { loadUnits } from "../../app/units/unitsStorage";
import { spacing } from "../../app/design-system";
import { SettingsListPage,type ColumnDefinition,type ProColumnsWithMeasure,type SettingsListActionRef, type SettingsListRequest } from "../../components/SettingsListPage";
import { ThemedModal } from "../../components/ThemedModal";
import { InboundEmailInboxTab } from "./InboundEmailInboxTab";
import { letterProviderRouteLabel } from "./letterProviderRouteLabel";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";
import { Permissions } from "../../app/constants/permissions";
import { CHANNEL_EMAIL, CHANNEL_LETTER, CHANNEL_LETTER_LC, CHANNEL_WHATSAPP } from "../../app/constants/communicationChannel";

/** Vollzugriff in der Rechteliste (Administratorrolle), siehe `app/auth/permissionMatch.ts`. */
const ALL_PERMISSIONS_WILDCARD = "*";

const inboxColumnWidths = {
  fromNumber: 160,
  toNumber: 160,
  personDisplayName: 160,
  studioName: 140,
  receivedAt: 170,
  letterRoute: 200,
  reply: 100,
} as const;

type InboundMessageDto = {
  id: string;
  channel: string;
  fromNumber: string;
  toNumber: string;
  body: string;
  personId?: string | null;
  personDisplayName?: string | null;
  studioId?: string | null;
  studioName?: string | null;
  receivedAt: string;
  createdAt: string;
  integrationId?: string | null;
  integrationDisplayName?: string | null;
  integrationType?: string | null;
};

type InboundListResponse = { total: number; data: InboundMessageDto[] };

const PAGE_SIZE = 20;
const BODY_PREVIEW_LEN = 80;

const INBOX_CHANNELS = ["Email", "Letter", "SMS", "WhatsApp"] as const;
type InboxChannelTab = (typeof INBOX_CHANNELS)[number];
type InboxNavigationState = { fromDashboard?: boolean; inboxTab?: InboxChannelTab };

function isInboxChannelTab(value: unknown): value is InboxChannelTab {
  return typeof value === "string" && INBOX_CHANNELS.includes(value as InboxChannelTab);
}

/** Brief, SMS und WhatsApp gibt es nur mit Integration oder vorhandenem Bestand. */
function isInboxChannelVisible(ch: InboxChannelTab, visible: ChannelFlags): boolean {
  switch (ch) {
    case "Letter":
      return visible.letter;
    case "SMS":
      return visible.sms;
    case "WhatsApp":
      return visible.whatsApp;
    default:
      return true;
  }
}

function inboxTabStorageKey(accountId: string | null | undefined): string {
  return `communication.inbox.activeTab.${(accountId || "anon").trim() || "anon"}`;
}

function loadInboxTab(accountId: string | null | undefined): InboxChannelTab {
  if (typeof window === "undefined") return "Email";
  try {
    const stored = window.localStorage.getItem(inboxTabStorageKey(accountId));
    return INBOX_CHANNELS.includes(stored as InboxChannelTab) ? stored as InboxChannelTab : "Email";
  } catch {
    return "Email";
  }
}

function saveInboxTab(accountId: string | null | undefined, tab: InboxChannelTab): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(inboxTabStorageKey(accountId), tab);
  } catch {
    // Browser storage may be unavailable; tab navigation still works.
  }
}

function inboxTabLabel(ch: InboxChannelTab, text: (key: string) => string): string {
  switch (ch) {
    case "SMS":
      return text("SMS");
    case "WhatsApp":
      return text("WhatsApp");
    case "Email":
      return text("Email");
    case "Letter":
      return text("Letter");
    default:
      return ch;
  }
}

export function InboxPage() {
  const auth = useAuth();
  const location = useLocation();
  const { text } = useI18n();
  const feedback = useFeedback();
  const actionRef = React.useRef<SettingsListActionRef>(null);
  const { studioIdsQueryParam } = useGlobalStudioContext();
  const units = React.useMemo(() => loadUnits(auth.info?.accountId), [auth.info?.accountId]);
  useRealtimeListRefresh({
    eventTypes: [RealtimeEventTypes.InboxChanged],
    reload: () => void actionRef.current?.revalidateCurrentQuery(),
    enabled: !!auth.token
  });

  const requestedInboxTab = new URLSearchParams(location.search).has("emailId") ? "Email" : isInboxChannelTab((location.state as InboxNavigationState | null)?.inboxTab)
    ? (location.state as InboxNavigationState).inboxTab
    : undefined;
  const [selectedChannel, setActiveChannel] = React.useState<InboxChannelTab>(() => requestedInboxTab ?? loadInboxTab(auth.info?.accountId));
  const channelAvailability = useChannelAvailability();
  // Ein gemerkter Reiter, den es (noch) nicht gibt, fällt auf E-Mail zurück.
  const activeChannel: InboxChannelTab = isInboxChannelVisible(selectedChannel, channelAvailability.isVisible)
    ? selectedChannel
    : "Email";
  const [replyModalOpen, setReplyModalOpen] = React.useState(false);
  const [replyTarget, setReplyTarget] = React.useState<InboundMessageDto | null>(null);
  const [replyBody, setReplyBody] = React.useState("");
  const [replySubmitting, setReplySubmitting] = React.useState(false);

  React.useEffect(() => {
    void actionRef.current?.revalidateCurrentQuery();
  }, [studioIdsQueryParam]);

  React.useEffect(() => {
    window.setTimeout(() => void actionRef.current?.revalidateFromFirstPage(), 0);
  }, [activeChannel]);

  React.useEffect(() => {
    setActiveChannel(requestedInboxTab ?? loadInboxTab(auth.info?.accountId));
  }, [auth.info?.accountId, requestedInboxTab]);

  const openReply = (record: InboundMessageDto) => {
    setReplyTarget(record);
    setReplyBody("");
    setReplyModalOpen(true);
  };

  const closeReply = () => {
    setReplyModalOpen(false);
    setReplyTarget(null);
    setReplyBody("");
  };

  const submitReply = async () => {
    if (!replyTarget || !replyBody.trim()) return;
    setReplySubmitting(true);
    try {
      await auth.api.post(`/api/v1/inbound-messages/${encodeURIComponent(replyTarget.id)}/reply`, {
        body: replyBody.trim(),
      });
      feedback.success(text("Reply sent to queue"));
      closeReply();
      void actionRef.current?.revalidateCurrentQuery();
    } catch {
      // Central API toast shows error
    } finally {
      setReplySubmitting(false);
    }
  };

  const hasSendPerm =
    auth.info?.permissions?.some((p: string) => p === Permissions.CommunicationSend || p === ALL_PERMISSIONS_WILDCARD) ?? false;

  const columnDefinitions: ColumnDefinition[] = React.useMemo(() => {
    const letterCol: ColumnDefinition[] =
      activeChannel === "Letter"
        ? [{ key: "letterRoute", label: "Inbox letter route", defaultVisible: true }]
        : [];
    return [
      ...letterCol,
      { key: "fromNumber", label: "From", defaultVisible: true },
      { key: "toNumber", label: "To", defaultVisible: true },
      { key: "body", label: "Body", defaultVisible: true },
      { key: "personDisplayName", label: "Person", defaultVisible: true },
      { key: "studioName", label: "Studio", defaultVisible: true },
      { key: "receivedAt", label: "Received at", defaultVisible: true },
    ];
  }, [activeChannel]);

  const allColumnsMap: Record<string, ProColumnsWithMeasure<InboundMessageDto>> = React.useMemo(() => {
    const base: Record<string, ProColumnsWithMeasure<InboundMessageDto>> = {
      fromNumber: {
        title: text("From"),
        dataIndex: "fromNumber",
        key: "fromNumber",
        width: inboxColumnWidths.fromNumber,
        ellipsis: true,
        sorter: true,
      },
      toNumber: {
        title: text("To"),
        dataIndex: "toNumber",
        key: "toNumber",
        width: inboxColumnWidths.toNumber,
        ellipsis: true,
        sorter: true,
      },
      body: {
        title: text("Body"),
        dataIndex: "body",
        key: "body",
        ellipsis: true,
        sorter: true,
        render: (_, r) => {
          const b = r.body ?? "";
          return b.length > BODY_PREVIEW_LEN ? `${b.slice(0, BODY_PREVIEW_LEN)}…` : b || text("—");
        },
      },
      personDisplayName: {
        title: text("Person"),
        dataIndex: "personDisplayName",
        key: "personDisplayName",
        width: inboxColumnWidths.personDisplayName,
        ellipsis: true,
        sorter: true,
        render: (_, r) => r.personDisplayName ?? text("—"),
      },
      studioName: {
        title: text("Studio"),
        dataIndex: "studioName",
        key: "studioName",
        width: inboxColumnWidths.studioName,
        sorter: true,
        render: (_, r) => r.studioName ?? text("—"),
      },
      receivedAt: {
        title: text("Received at"),
        dataIndex: "receivedAt",
        key: "receivedAt",
        width: inboxColumnWidths.receivedAt,
        sorter: true,
        defaultSortOrder: "descend",
        render: (_, r) =>
          r.receivedAt ? (
            formatDateTimeNoSeconds(r.receivedAt, units)
          ) : (
            <Typography.Text type="secondary">{text("—")}</Typography.Text>
          ),
      },
    };
    if (activeChannel === "Letter") {
      return {
        letterRoute: {
          title: text("Inbox letter route"),
          dataIndex: "integrationDisplayName",
          key: "letterRoute",
          width: inboxColumnWidths.letterRoute,
          ellipsis: true,
          sorter: true,
          render: (_, r) => letterProviderRouteLabel(r.integrationDisplayName, r.integrationType, text),
        },
        ...base,
      };
    }
    return base;
  }, [activeChannel, text, units]);

  const actionsColumn: ListColumn<InboundMessageDto> | undefined =
    hasSendPerm && activeChannel !== "Letter" && activeChannel !== "Email"
      ? {
          title: "",
          key: "reply",
          width: inboxColumnWidths.reply,
          sorter: false,
          fixed: "right" as const,
          render: (_: unknown, record: InboundMessageDto) => (
            <Button type="link" size="small" icon={<MessageOutlined />} onClick={() => openReply(record)}>
              {text("Reply")}
            </Button>
          ),
        }
      : undefined;

  const tabItems = React.useMemo(
    () =>
      INBOX_CHANNELS.filter((ch) => isInboxChannelVisible(ch, channelAvailability.isVisible)).map((ch) => ({
        key: ch,
        label: inboxTabLabel(ch, text),
      })),
    [channelAvailability.isVisible, text]
  );

  const storageKey = `communication.inbox.${activeChannel.toLowerCase()}`;

  const requestMessages = React.useCallback<SettingsListRequest<InboundMessageDto>>(async (params, sort) => {
    const current = params?.current ?? 1;
    const pageSize = params?.pageSize ?? PAGE_SIZE;
    const skip = (current - 1) * pageSize;
    const searchParams = new URLSearchParams();
    if (studioIdsQueryParam) searchParams.set("studioIds", studioIdsQueryParam);
    searchParams.set("channels", activeChannel);
    searchParams.set("skip", String(skip));
    searchParams.set("take", String(pageSize));
    const activeSort = getActiveRequestSort(sort);
    if (activeSort) {
      searchParams.set("sortBy", activeSort.field);
      searchParams.set("sortOrder", activeSort.direction);
    }
    const res = await auth.api.get<InboundListResponse>(
      `/api/v1/inbound-messages?${searchParams.toString()}`
    );
    return {
      data: res?.data ?? [],
      success: true,
      total: res?.total ?? 0,
    };
  }, [auth.api, studioIdsQueryParam, activeChannel]);

  return (
    <PageFrame title={text("Inbox")}><div>{/* Ohne Schnittstelle bleibt nur E-Mail — dann braucht es keine Reiterleiste. */}{tabItems.length > 1 ? (
        <Tabs
          className="communication-inbox-tabs"
          activeKey={activeChannel}
          onChange={(key) => {
            const next = key as InboxChannelTab;
            setActiveChannel(next);
            saveInboxTab(auth.info?.accountId, next);
          }}
          items={tabItems}
          style={{ marginBottom: spacing.elementGap }}
        />
      ) : null}{activeChannel === "Email" ? (
        <InboundEmailInboxTab />
      ) : (
        <SettingsListPage<InboundMessageDto>
          dataMode="server-query"
          storageKey={storageKey}
          rowKey="id"
          request={requestMessages}
          columnDefinitions={columnDefinitions}
          allColumnsMap={allColumnsMap}
          actionsColumn={actionsColumn}
          actionRef={actionRef}
          pagination={{ pageSize: PAGE_SIZE }}
          sectionTitle={null}
        />
      )}<ThemedModal
        title={text("Reply")}
        open={replyModalOpen}
        onCancel={closeReply}
        onOk={submitReply}
        okText={text("Send")}
        cancelText={text("Cancel")}
        submitting={replySubmitting}
        destroyOnClose
        submitOnEnter={false}
        okButtonProps={{ disabled: !replyBody.trim() }}
      >
        {replyTarget && (
          <div style={{ marginBottom: spacing.elementGap }}>
            <Typography.Text type="secondary">
              {text("Reply to")}: {replyTarget.fromNumber}
              {replyTarget.personDisplayName ? ` (${replyTarget.personDisplayName})` : ""} –{" "}
              {replyTarget.channel === CHANNEL_WHATSAPP
                ? text("WhatsApp")
                : replyTarget.channel === CHANNEL_EMAIL
                  ? text("Email")
                  : replyTarget.channel === CHANNEL_LETTER || replyTarget.channel?.toLowerCase() === CHANNEL_LETTER_LC
                    ? text("Letter")
                    : text("SMS")}
            </Typography.Text>
          </div>
        )}
        <textarea
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          placeholder={text("Your reply")}
          rows={4}
          style={{ width: "100%", resize: "vertical" }}
        />
      </ThemedModal></div></PageFrame>
  );
}
